'use client';

import * as React from 'react';
import { XCloseIcon } from '@newcore/icons';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { FontWeights } from '../enums/font-weights';
import { Sizes } from '../enums/sizes';
import { TextSizes } from '../enums/typography-text-sizes';
import { cn } from '../utils/cn';
import { dialogContentVariants } from '../variants/dialog-variants';
import { textVariants } from '../variants/typography-text-variants';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-100 bg-black/60 backdrop-blur-sm duration-200 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogPrimitiveContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
>;

export type DialogSize = Sizes.MD | Sizes.LG;

interface DialogContentProps extends DialogPrimitiveContentProps {
  preventClose?: boolean;
  size?: DialogSize;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    { className, children, preventClose = false, size = Sizes.MD, ...props },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ size }), className)}
        onInteractOutside={preventClose ? e => e.preventDefault() : undefined}
        onEscapeKeyDown={preventClose ? e => e.preventDefault() : undefined}
        {...props}
      >
        <DialogPrimitive.Title className='sr-only'>
          Dialog
        </DialogPrimitive.Title>
        <DialogPrimitive.Description className='sr-only'>
          Dialog content
        </DialogPrimitive.Description>
        {children}
        <DialogPrimitive.Close
          data-slot='dialog-close'
          className='absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:not-disabled:opacity-100 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed data-[state=open]:bg-secondary_hover data-[state=open]:text-tertiary'
          disabled={preventClose}
        >
          <XCloseIcon />
          <span className='sr-only'>Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      textVariants({ size: TextSizes.LG, weight: FontWeights.SEMIBOLD }),
      'leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-tertiary', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
